// Straight line segments in an image, LSD style (von Gioi et al.): pixels taken strongest gradient first, each
// seed grows a region of neighbours whose gradient points the same way (within angTol), the region's rectangle is
// fitted, and it is kept only when it has too many aligned pixels to be chance (the number of false alarms, NFA,
// under 1). The line is then refitted by total least squares through the sub-pixel edge points (the gradient
// peak across it). Segments run with the brighter side on their left (so direction says polarity), and keep the
// grey profile either side for matching (match.ts). chains links segments that continue each other: a long edge
// a lens bends a little falls into several (distort.ts).
import { type Gray, type Grads, type Mask2, bilinear, blur, grads } from './image.ts';

export interface Segs {
  n: number;
  /** x0 y0 x1 y1 per segment (photo pixels), the brighter side on the left of (x0, y0) → (x1, y1). */
  seg: Float32Array;
  /** Mean gradient along it. */
  contrast: Float32Array;
  /** Mean grey at 1.5, 3, 4.5, 6 px on its left, then the same on its right: 8 per segment. */
  side: Float32Array;
  /** log10 of its NFA (more negative: surer). */
  lnfa: Float32Array;
}

export interface SegOpts {
  /** Gradient directions within this of the region's are aligned (degrees). */
  angTol?: number;
  /** Shortest kept (px). */
  minLen?: number;
  /** Share of the rectangle's pixels the region must fill. */
  density?: number;
  /** Weakest gradient that counts (grey units per px). */
  gradMin?: number;
  /** Pre-blur (px; 0: none). */
  sigma?: number;
  /** Only pixels on the mask seed or join regions. */
  mask?: Mask2 | null;
  /** Keep when log10 NFA is below this. */
  eps?: number;
  /** Raise gradMin to 1.5 × the median gradient (default on; off for depth images, mostly flat). */
  adapt?: boolean;
}

const SIDE = [1.5, 3, 4.5, 6];

/** log10 of the binomial tail P[X >= k], X ~ Bin(n, p). */
export function logTail(n: number, k: number, p: number): number {
  if (k <= 0) return 0;
  if (k > n) return -Infinity;
  const lg = (x: number) => lgamma(x + 1);
  const term = (i: number) => lg(n) - lg(i) - lg(n - i) + i * Math.log(p) + (n - i) * Math.log1p(-p);
  let t = term(k), s = 1;   // sum of exp(term(i) - term(k))
  for (let i = k + 1, r = 0; i <= n; i++) {
    r += Math.log(((n - i + 1) / i) * (p / (1 - p)));
    const e = Math.exp(r);
    s += e;
    if (e < 1e-12 * s) break;
  }
  return (t + Math.log(s)) / Math.LN10;
}

/** ln Γ(x) (Lanczos). */
export function lgamma(x: number): number {
  const c = [76.18009172947146, -86.50532032941677, 24.01409824083091, -1.231739572450155, 0.1208650973866179e-2, -0.5395239384953e-5];
  let y = x, t = x + 5.5;
  t -= (x + 0.5) * Math.log(t);
  let s = 1.000000000190015;
  for (const q of c) s += q / ++y;
  return -t + Math.log((2.5066282746310005 * s) / x);
}

const wrap = (a: number) => { while (a > Math.PI) a -= 2 * Math.PI; while (a < -Math.PI) a += 2 * Math.PI; return a; };

/** Line segments of g. */
export function lsd(g0: Gray, o: SegOpts = {}): Segs {
  const tol = ((o.angTol ?? 22.5) * Math.PI) / 180, minLen = o.minLen ?? 15, dens = o.density ?? 0.7;
  let gmin = o.gradMin ?? 0.02;
  const eps = o.eps ?? 0, mask = o.mask ?? null;
  const g = (o.sigma ?? 0.6) > 0 ? blur(g0, o.sigma ?? 0.6) : g0, { w, h } = g, gr = grads(g), N = w * h;
  const ang = new Float32Array(N);
  let top = 0;
  for (let i = 0; i < N; i++) { ang[i] = Math.atan2(gr.gy[i], gr.gx[i]); if (gr.mag[i] > top) top = gr.mag[i]; }
  // the floor rises with the image's own texture (its median gradient: JPEG noise, wallpaper), so regions don't spread into it
  if (o.adapt ?? true) { const q = gr.mag.filter((_, i) => i % 7 === 0).sort(); gmin = Math.max(gmin, 1.5 * q[q.length >> 1]); }
  // pixels by gradient, strongest first (a pseudo-sort into bins)
  const BINS = 1024, cnt = new Int32Array(BINS + 1);
  const bin = (m: number) => Math.min(BINS - 1, Math.floor(((m - gmin) / Math.max(1e-9, top - gmin)) * BINS));
  for (let i = 0; i < N; i++) if (gr.mag[i] > gmin && (!mask || mask[i])) cnt[bin(gr.mag[i])]++;
  const start = new Int32Array(BINS);
  for (let b = BINS - 1, s = 0; b >= 0; b--) { start[b] = s; s += cnt[b]; }
  const sorted = new Int32Array(start[0] + cnt[0] || 0);
  { const at = start.slice(); for (let i = 0; i < N; i++) if (gr.mag[i] > gmin && (!mask || mask[i])) sorted[at[bin(gr.mag[i])]++] = i; }
  const used = new Uint8Array(N);
  for (let i = 0; i < N; i++) if (gr.mag[i] <= gmin || (mask && !mask[i])) used[i] = 2;   // never joins
  const p = tol / Math.PI, logNT = 2.5 * Math.log10(w * h) + Math.log10(11);   // number of tests: rectangles × widths

  const out: { s: number[]; c: number; lnfa: number }[] = [];
  const region: number[] = [];
  const grow = (seed: number, t: number) => {
    region.length = 0;
    region.push(seed); used[seed] = 1;
    let sx = Math.cos(ang[seed]), sy = Math.sin(ang[seed]), ra = ang[seed];
    for (let r = 0; r < region.length; r++) {
      const i = region[r], x = i % w, y = (i - x) / w;
      for (let j = -1; j <= 1; j++) for (let k = -1; k <= 1; k++) {
        const xx = x + k, yy = y + j;
        if ((!j && !k) || xx < 0 || yy < 0 || xx >= w || yy >= h) continue;
        const q = yy * w + xx;
        if (used[q] || Math.abs(wrap(ang[q] - ra)) > t) continue;
        used[q] = 1; region.push(q);
        sx += Math.cos(ang[q]); sy += Math.sin(ang[q]); ra = Math.atan2(sy, sx);
      }
    }
    return ra;
  };
  const small = Math.max(5, minLen / 2);
  for (const seed of sorted) {
    if (used[seed]) continue;
    let ra = grow(seed, tol);
    if (region.length < small) continue;   // stays used, as in LSD
    let rect = rectOf(region, gr, w);
    const dense = () => region.length / Math.max(1, rect.len * rect.wid) >= dens;
    if (!dense()) {
      // too loose (texture alongside, a curve, two lines meeting): as LSD, regrow with the spread of the angles
      // near the seed as the tolerance, then shrink it around the seed until it is dense or too small
      const sx = seed % w, sy = (seed - sx) / w, near: number[] = [];
      for (const i of region) { const x = i % w, y = (i - x) / w; if (Math.hypot(x - sx, y - sy) <= rect.wid) near.push(wrap(ang[i] - ra)); }
      const sd = Math.sqrt(near.reduce((a, d) => a + d * d, 0) / Math.max(1, near.length));
      for (const i of region) used[i] = 0;
      ra = grow(seed, Math.min(tol, Math.max(tol / 4, 2 * sd)));
      if (region.length < small) continue;
      rect = rectOf(region, gr, w);
      let rad = 0;
      for (const i of region) { const x = i % w; rad = Math.max(rad, Math.hypot(x - sx, (i - x) / w - sy)); }
      while (!dense() && region.length >= small) {
        rad *= 0.75;
        const keep: number[] = [];
        for (const i of region) { const x = i % w; if (Math.hypot(x - sx, (i - x) / w - sy) <= rad) keep.push(i); else used[i] = 0; }
        region.length = 0; region.push(...keep);
        if (region.length < small) break;
        rect = rectOf(region, gr, w);
        let cs = 0, sn = 0;
        for (const i of region) { cs += Math.cos(ang[i]); sn += Math.sin(ang[i]); }
        ra = Math.atan2(sn, cs);
      }
      if (region.length < small || !dense()) continue;
    }
    if (rect.len < minLen) continue;
    // NFA: aligned pixels in the rectangle, against chance p
    const { n, k } = alignedIn(rect, ang, w, h, tol, ra);
    const lnfa = logNT + logTail(n, k, p);
    if (lnfa >= eps) continue;
    const fit = refit(g, gr, region, w, h, ra);
    if (!fit || Math.hypot(fit[2] - fit[0], fit[3] - fit[1]) < minLen) continue;
    let c = 0;
    for (const i of region) c += gr.mag[i];
    out.push({ s: fit, c: c / region.length, lnfa });
  }
  const n = out.length, seg = new Float32Array(4 * n), contrast = new Float32Array(n), side = new Float32Array(8 * n), lnfa = new Float32Array(n);
  out.forEach((r, k) => {
    seg.set(r.s, 4 * k); contrast[k] = r.c; lnfa[k] = r.lnfa;
    side.set(profile(g0, r.s), 8 * k);
  });
  return { n, seg, contrast, side, lnfa };
}

interface Rect { cx: number; cy: number; dx: number; dy: number; l0: number; l1: number; w0: number; w1: number; len: number; wid: number }

/** The region's rectangle: centre by gradient-weighted mean, direction by its weighted inertia. */
function rectOf(region: number[], gr: Grads, w: number): Rect {
  let sw = 0, cx = 0, cy = 0;
  for (const i of region) { const m = gr.mag[i], x = i % w, y = (i - x) / w; sw += m; cx += m * x; cy += m * y; }
  cx /= sw; cy /= sw;
  let a = 0, b = 0, c = 0;
  for (const i of region) { const m = gr.mag[i], x = (i % w) - cx, y = Math.floor(i / w) - cy; a += m * x * x; b += m * x * y; c += m * y * y; }
  const th = 0.5 * Math.atan2(2 * b, a - c), dx = Math.cos(th), dy = Math.sin(th);
  let l0 = Infinity, l1 = -Infinity;
  const ts = new Float32Array(region.length);
  region.forEach((i, k) => {
    const x = (i % w) - cx, y = Math.floor(i / w) - cy, l = x * dx + y * dy;
    ts[k] = -x * dy + y * dx;
    if (l < l0) l0 = l; if (l > l1) l1 = l;
  });
  // width from the 5th to 95th percentile across: a stray pixel or two doesn't make it wide
  ts.sort();
  const w0 = ts[Math.floor(0.05 * (ts.length - 1))], w1 = ts[Math.ceil(0.95 * (ts.length - 1))];
  return { cx, cy, dx, dy, l0, l1, w0, w1, len: l1 - l0 + 1, wid: w1 - w0 + 1 };
}

/** Pixels in the rectangle (n) and how many have their gradient within tol of the region's (k). */
function alignedIn(r: Rect, ang: Float32Array, w: number, h: number, tol: number, ra: number) {
  let n = 0, k = 0;
  const ex = Math.abs(r.dx) * (r.l1 - r.l0) / 2 + Math.abs(r.dy) * (r.w1 - r.w0) / 2 + 1, ey = Math.abs(r.dy) * (r.l1 - r.l0) / 2 + Math.abs(r.dx) * (r.w1 - r.w0) / 2 + 1;
  const mx = r.cx + r.dx * (r.l0 + r.l1) / 2 - r.dy * (r.w0 + r.w1) / 2, my = r.cy + r.dy * (r.l0 + r.l1) / 2 + r.dx * (r.w0 + r.w1) / 2;
  for (let y = Math.max(0, Math.floor(my - ey)); y <= Math.min(h - 1, Math.ceil(my + ey)); y++)
    for (let x = Math.max(0, Math.floor(mx - ex)); x <= Math.min(w - 1, Math.ceil(mx + ex)); x++) {
      const px = x - r.cx, py = y - r.cy, l = px * r.dx + py * r.dy, t = -px * r.dy + py * r.dx;
      if (l < r.l0 - 0.5 || l > r.l1 + 0.5 || t < r.w0 - 0.5 || t > r.w1 + 0.5) continue;
      n++;
      if (Math.abs(wrap(ang[y * w + x] - ra)) <= tol) k++;
    }
  return { n, k };
}

/**
 * Total least squares through the region's sub-pixel edge points (gradient peak along the gradient, a
 * parabola through 3 samples), weighted by gradient; endpoints from the points' extent. Oriented so the
 * gradient (dark → bright) is the segment's left normal.
 */
function refit(g: Gray, gr: Grads, region: number[], w: number, h: number, ra: number): number[] | null {
  const gx = Math.cos(ra), gy = Math.sin(ra);
  const pts: number[] = [], wt: number[] = [];
  for (const i of region) {
    const x = i % w, y = (i - x) / w;
    const m0 = bilinear(gr.mag, w, h, x - gx, y - gy), m1 = gr.mag[i], m2 = bilinear(gr.mag, w, h, x + gx, y + gy);
    if (m1 < m0 || m1 < m2) continue;   // not the peak across the edge: its neighbour is
    const den = m0 - 2 * m1 + m2, off = den < 0 ? Math.max(-0.5, Math.min(0.5, (0.5 * (m0 - m2)) / den)) : 0;
    pts.push(x + off * gx, y + off * gy); wt.push(m1);
  }
  if (pts.length < 4) return null;
  const L = tls(pts, wt);
  // direction: the gradient along the left normal (dy, -dx), as profile() takes it
  let dx = L.dx, dy = L.dy;
  if (dy * gx - dx * gy < 0) { dx = -dx; dy = -dy; }
  let l0 = Infinity, l1 = -Infinity;
  for (let i = 0; i < pts.length; i += 2) { const l = (pts[i] - L.cx) * dx + (pts[i + 1] - L.cy) * dy; if (l < l0) l0 = l; if (l > l1) l1 = l; }
  return [L.cx + l0 * dx, L.cy + l0 * dy, L.cx + l1 * dx, L.cy + l1 * dy];
}

/** Weighted total least squares line through packed 2D points: centre, direction, rms off it. */
export function tls(pts: ArrayLike<number>, wt?: ArrayLike<number>) {
  let sw = 0, cx = 0, cy = 0;
  const n = pts.length >> 1;
  for (let i = 0; i < n; i++) { const q = wt ? wt[i] : 1; sw += q; cx += q * pts[2 * i]; cy += q * pts[2 * i + 1]; }
  cx /= sw; cy /= sw;
  let a = 0, b = 0, c = 0;
  for (let i = 0; i < n; i++) { const q = wt ? wt[i] : 1, x = pts[2 * i] - cx, y = pts[2 * i + 1] - cy; a += q * x * x; b += q * x * y; c += q * y * y; }
  const th = 0.5 * Math.atan2(2 * b, a - c), dx = Math.cos(th), dy = Math.sin(th);
  const m = 0.5 * (a + c), d = Math.sqrt((0.5 * (a - c)) ** 2 + b * b);
  return { cx, cy, dx, dy, rms: Math.sqrt(Math.max(0, m - d) / sw) };
}

/** Mean grey either side of a segment, at SIDE px out (left, then right). */
function profile(g: Gray, s: ArrayLike<number>): number[] {
  const dx = s[2] - s[0], dy = s[3] - s[1], L = Math.hypot(dx, dy), ux = dx / L, uy = dy / L, nx = uy, ny = -ux;   // n: left
  const out = new Array(8).fill(0), steps = Math.max(2, Math.floor(L / 2));
  for (let t = 0; t < steps; t++) {
    const f = (t + 0.5) / steps, x = s[0] + f * dx, y = s[1] + f * dy;
    SIDE.forEach((d, k) => { out[k] += bilinear(g.d, g.w, g.h, x + d * nx, y + d * ny); out[4 + k] += bilinear(g.d, g.w, g.h, x - d * nx, y - d * ny); });
  }
  return out.map(v => v / steps);
}

/**
 * Sub-pixel edge points along a segment: every step px, the gradient peak within ±r px across it (parabola);
 * packed x, y. Unlike the segment itself these follow a bent edge.
 */
export function edgePts(gr: Grads, w: number, h: number, s: ArrayLike<number>, step = 1, r = 2, trim = 3): Float32Array {
  const dx = s[2] - s[0], dy = s[3] - s[1], L = Math.hypot(dx, dy), ux = dx / L, uy = dy / L, nx = uy, ny = -ux, out: number[] = [];
  // not the ends (where another edge meets it, its gradient takes over), and only peaks whose gradient points across it
  for (let t = Math.min(trim, L / 4); t <= L - Math.min(trim, L / 4); t += step) {
    const x = s[0] + t * ux, y = s[1] + t * uy;
    let best = -1, bm = -1;
    const m: number[] = [];
    for (let k = -r; k <= r; k++) { const v = bilinear(gr.mag, w, h, x + k * nx, y + k * ny); m.push(v); if (v > bm) { bm = v; best = k; } }
    if (best === -r || best === r) continue;
    const a = m[best + r - 1], b = m[best + r], c = m[best + r + 1], den = a - 2 * b + c;
    const off = best + (den < 0 ? (0.5 * (a - c)) / den : 0), px = x + off * nx, py = y + off * ny;
    const gx = bilinear(gr.gx, w, h, px, py), gy = bilinear(gr.gy, w, h, px, py), gl = Math.hypot(gx, gy);
    if (!gl || Math.abs(gx * nx + gy * ny) / gl < 0.95) continue;
    out.push(px, py);
  }
  return Float32Array.from(out);
}

export interface ChainOpts { gap?: number; ang?: number; off?: number }

/**
 * Segments that continue each other (end near the next one's start, same direction within ang°, the next one's
 * start within off px of the line), linked into chains (lists of segment indices, in order; lone ones too).
 */
export function chains(s: Segs, o: ChainOpts = {}): number[][] {
  const gap = o.gap ?? 12, ang = ((o.ang ?? 4) * Math.PI) / 180, off = o.off ?? 2.5;
  const next = new Int32Array(s.n).fill(-1), prev = new Int32Array(s.n).fill(-1), S = s.seg;
  const dir = (i: number) => Math.atan2(S[4 * i + 3] - S[4 * i + 1], S[4 * i + 2] - S[4 * i]);
  const cand: [number, number, number][] = [];
  for (let a = 0; a < s.n; a++) for (let b = 0; b < s.n; b++) {
    if (a === b) continue;
    const ex = S[4 * a + 2], ey = S[4 * a + 3], d = Math.hypot(S[4 * b] - ex, S[4 * b + 1] - ey);
    if (d > gap || Math.abs(wrap(dir(a) - dir(b))) > ang) continue;
    const ux = Math.cos(dir(a)), uy = Math.sin(dir(a)), along = (S[4 * b] - ex) * ux + (S[4 * b + 1] - ey) * uy;
    if (along < -3) continue;   // overlapping a lot: two edges side by side, not one going on
    if (Math.abs(-(S[4 * b] - ex) * uy + (S[4 * b + 1] - ey) * ux) > off) continue;
    cand.push([d, a, b]);
  }
  cand.sort((x, y) => x[0] - y[0]);
  for (const [, a, b] of cand) if (next[a] < 0 && prev[b] < 0) {
    let c = b;   // no loops
    while (c >= 0 && c !== a) c = next[c];
    if (c === a) continue;
    next[a] = b; prev[b] = a;
  }
  const out: number[][] = [];
  for (let i = 0; i < s.n; i++) if (prev[i] < 0) { const ch = [i]; for (let c = next[i]; c >= 0; c = next[c]) ch.push(c); out.push(ch); }
  return out;
}

/** The segments with indices in keep (a subset, same layout). */
export function pickSegs(s: Segs, keep: number[]): Segs {
  const n = keep.length, o: Segs = { n, seg: new Float32Array(4 * n), contrast: new Float32Array(n), side: new Float32Array(8 * n), lnfa: new Float32Array(n) };
  keep.forEach((i, k) => {
    o.seg.set(s.seg.subarray(4 * i, 4 * i + 4), 4 * k); o.contrast[k] = s.contrast[i]; o.lnfa[k] = s.lnfa[i];
    o.side.set(s.side.subarray(8 * i, 8 * i + 8), 8 * k);
  });
  return o;
}
